import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../TextInput/TextInput";
import CustomSelect from "../Select/Select";
import AllSocialPublish from "../MediaPublish/SocialPublish/SocialPublish";
import {
  DenmarkPublish as DenmarkPublishInterface,
  SocialPresetModel,
} from "@/database";
import { toast } from "..";
interface Props {
  liveList?: any;
  item?: any;
  onSubmit?: (payload: any) => void;
  businessProductConstants?: any;
  business?: any;
  selectedMedia?: any;
  isEdit?: boolean;
  productList?: any;
  setIsSocialBtnDisabled?: (isDisabled: boolean) => void;
  presetList?: any;
  singleCurrentUser?: any;
}

const SocialPresets = ({
  item,
  onSubmit,
  selectedMedia,
  isEdit = false,
  productList,
  setIsSocialBtnDisabled,
  presetList,
}: Props) => {
  const [allSocialPresets, setAllSocialPresets] = useState<SocialPresetModel[]>(
    []
  );
  const [initialAllSocialPresets, setInitialAllSocialPresets] = useState<
    SocialPresetModel[]
  >([]);
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({
    video_flavour: false,
    audio_flavour: false,
    encryption: false,
    generate_video_preview: false,
    generate_ai_thumbnail: false,
    generate_embedded_podcast: false,
    audio_waveform: false,
    generate_transcript: false,
  });

  const handleAllSocialPresets = useCallback((presets: SocialPresetModel[]) => {
    setAllSocialPresets(presets);
  }, []);

  const deepEqualIgnoreExtraKeys3 = (a: any, b: any): boolean => {
    // If both are arrays → compare item by item up to the shortest length
    if (a.length !== b.length) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      const minLength = Math.min(a.length, b.length);
      for (let i = 0; i < minLength; i++) {
        if (!deepEqualIgnoreExtraKeys3(a[i], b[i])) return false;
      }
      return true;
    }

    // If both are objects → only compare intersection of keys
    if (typeof a === "object" && typeof b === "object" && a && b) {
      const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])]; // union of keys
      for (const key of keys) {
        // if either side doesn't have the key → skip
        if (!(key in a) || !(key in b)) continue;

        if (!deepEqualIgnoreExtraKeys3(a[key], b[key])) return false;
      }
      return true;
    }

    // If either is undefined → skip comparison
    if (a === undefined || b === undefined) return true;

    // Fallback: strict comparison
    return a === b;
  };

  const deepEqualIgnoreExtraKeys = (a: any, b: any): boolean => {
    // If both are arrays → compare item by item
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => deepEqualIgnoreExtraKeys(item, b[index]));
    }

    // If both are objects → compare only keys present in `a`
    if (typeof a === "object" && typeof b === "object" && a && b) {
      return Object.keys(a).every((key) =>
        deepEqualIgnoreExtraKeys(a[key], b[key])
      );
    }

    // Fallback: direct comparison
    return a === b;
  };
  const deepEqualLenient = (a: any, b: any): boolean => {
    if (a === b) return true;

    if (typeof a !== typeof b) return false;

    if (typeof a === "object" && a !== null && b !== null) {
      const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);

      return Array.from(allKeys).every((key) => {
        const valA = a[key] === undefined ? null : a[key];
        const valB = b[key] === undefined ? null : b[key];
        return deepEqualLenient(valA, valB);
      });
    }

    return false;
  };

  const deepEqual2 = (a: any, b: any): boolean => {
    if (a === b) return true;

    if (typeof a !== typeof b) return false;

    if (typeof a === "object" && a !== null && b !== null) {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);

      if (aKeys.length !== bKeys.length) return false;

      return aKeys.every((key) => deepEqual2(a[key], b[key]));
    }

    return false;
  };

  const deepEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((val, index) => deepEqual(val, b[index]));
    }

    if (typeof a === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => deepEqual(a[key], b[key]));
    }

    return a === b;
  };

  // Deep comparison function to check if social presets have changed
  const deepCompareSocialPresets = (
    current: SocialPresetModel[],
    initial: SocialPresetModel[]
  ): boolean => {
    if (current.length !== initial.length) {
      return false;
    }

    return current.every((currentPreset, index) => {
      const initialPreset = initial[index];
      if (!initialPreset) return false;

      // Compare all properties of the preset using JSON.stringify for deep comparison
      return JSON.stringify(currentPreset) === JSON.stringify(initialPreset);
    });
  };

  // Helper function to get only enabled fields for submission
  const getEnabledFieldsPayload = (values: any) => {
    const payload: any = {
      presetName: values.presetName, // Always include preset name
    };

    // Only include fields that are enabled
    Object.keys(enabledFields).forEach((fieldName) => {
      if (
        enabledFields[fieldName] &&
        values[fieldName] !== undefined &&
        values[fieldName] !== ""
      ) {
        // Handle arrays properly - don't include empty arrays
        if (Array.isArray(values[fieldName])) {
          if (values[fieldName].length > 0) {
            payload[fieldName] = values[fieldName];
          }
        } else {
          payload[fieldName] = values[fieldName];
        }
      }
    });

    return payload;
  };

  // Initialize enabled fields based on item prop

  interface SocialPresetForm {
    presetName: string;
    visibility: string[];
    status: string | number;
  }

  const formik = useFormik<SocialPresetForm>({
    // enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      presetName: item?.name || selectedMedia?.name || "",
      visibility: Array.isArray(item?.team)
        ? item?.team
        : Array.isArray(selectedMedia?.visibility)
        ? selectedMedia?.visibility
        : [],
      status: item?.status || selectedMedia?.status || "",
    },
    validationSchema: Yup.object({
      presetName: Yup.string()
        .required("Preset Name is required")
        .test(
          "unique-preset-name",
          "Same Name is not allowed",
          function (value) {
            if (!value || !presetList || presetList.length === 0) return true;

            const currentVisibility = this.parent.visibility || [];

            // If no visibility is selected, don't check for duplicates
            // if (currentVisibility.length === 0) {
            //   return true;
            // }

            const duplicateExists = presetList.some((presetItem: any) => {
              // Check if names match
              if (presetItem.name !== value) return false;

              // Check if type matches
              if (presetItem.type !== "social") return false;

              // Skip self in edit mode
              if (isEdit && presetItem.id === item?.id) return false;

              // Check visibility/team match - compare arrays properly
              const itemteam = presetItem.team || [];

              // If item has no team, don't match  s
              if (itemteam.length === 0) {
                return false;
              }

              // Check if any visibility value matches any team value
              return currentVisibility.some((vis: string) =>
                itemteam.includes(vis)
              );
            });

            return !duplicateExists;
          }
        ),
      visibility: Yup.array().of(Yup.string()).min(1, "Visibility is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      // formik.setFieldError('presetName', errors.presetName);
      // formik.setFieldTouched('presetName', true);
      // formik.handleBlur('presetName');
      if (Object.keys(errors).length > 0) {
        return;
      }
      const payload = getEnabledFieldsPayload(values);

      if (allSocialPresets.length > 0) {
        const data = {
          name: values.presetName,
          team: values.visibility,
          type: "social",
          sub_type: "",
          status: values.status,
          social: allSocialPresets,
        };

        if (onSubmit) {
          onSubmit(data);
        }
      } else {
        toast.fail("Please select at least one Social Card");
      }
    },
  });

  const getBasePermission = (permission: string): string => {
    return permission;
  };

  // Initialize initialAllSocialPresets when component mounts or item changes
  useEffect(() => {
    const initialPresets = isEdit
      ? selectedMedia?.social
      : selectedMedia?.socials || [];
    setInitialAllSocialPresets(initialPresets);
  }, [isEdit, selectedMedia?.social, selectedMedia?.socials]);

  // Effect to control footer button state based on social presets changes
  useEffect(() => {
    const initialValues = formik.initialValues;
    const currentValues = formik.values;
    // Check if there are social presets and if they have changed from initial state
    // const hasSocialPresets = allSocialPresets.length > 0;

    const hasChanged = !deepEqualIgnoreExtraKeys3(
      allSocialPresets,
      initialAllSocialPresets
    );

    const basicFields = ["presetName", "status", "visibility"];
    const basicFieldsHaveChanges = basicFields.some((field) => {
      const initial = initialValues[field as keyof SocialPresetForm];
      const current = currentValues[field as keyof SocialPresetForm];
      return !deepEqual2(initial, current);
    });

    // Enable button only if there are presets AND they have changed from initial state
    // This prevents enabling the button when the form is in its initial state
    const shouldEnableButton = hasChanged || basicFieldsHaveChanges;
    setIsSocialBtnDisabled?.(!shouldEnableButton);
  }, [
    allSocialPresets,
    initialAllSocialPresets,
    formik.values,
    formik.initialValues,
    setIsSocialBtnDisabled,
  ]);

  function processPermissionForSelect(array: string[]) {
    const newarray = array?.map((str) => {
      return getBasePermission(str);
    });
    return newarray;
  }

  const handlePermissionChange = (values: string[], key: string) => {
    const newProducers = values?.map((value) => value);

    formik.setFieldValue(key, newProducers);
  };

  const handleCreateMetaPreset = useCallback(async () => {
    // First validate the current form
    const currentFormErrors = await formik.validateForm();

    // Set field errors and touched states for current form
    Object.keys(currentFormErrors).forEach((field) => {
      const error = currentFormErrors[field as keyof typeof currentFormErrors];
      formik.setFieldError(
        field,
        Array.isArray(error) ? error.join(", ") : error
      );
      formik.setFieldTouched(field, true);
    });

    // If current form has errors, don't proceed to child form validation
    // if (Object.keys(currentFormErrors).length > 0) {
    //   return;
    // }

    // Create a promise to collect validation results from all child forms
    const validateChildForms = new Promise<{
      allValid: boolean;
      validationResults: Array<{
        isValid: boolean;
        formId: string;
        errors: any;
      }>;
    }>((resolve) => {
      const validationResults: Array<{
        isValid: boolean;
        formId: string;
        errors: any;
      }> = [];

      const handleValidationResult = (event: CustomEvent) => {
        validationResults.push({
          isValid: event.detail.isValid,
          formId: event.detail.formId,
          errors: event.detail.errors,
        });
      };

      // Listen for validation results from child forms
      window.addEventListener(
        "validationResult",
        handleValidationResult as EventListener
      );

      // Trigger validation for all preset forms
      const validateEvent = new CustomEvent("validatePresetForms");
      window.dispatchEvent(validateEvent);

      // Wait for all validations to complete
      setTimeout(() => {
        window.removeEventListener(
          "validationResult",
          handleValidationResult as EventListener
        );
        const allValid =
          validationResults.length === 0 ||
          validationResults.every(
            (result: { isValid: boolean }) => result.isValid
          );
        resolve({ allValid, validationResults });
      }, 200);
    });

    // Wait for child form validations to complete
    validateChildForms.then(({ allValid }) => {
      if (allValid) {
        formik.handleSubmit();
      } else {
        //
      }
    });
  }, [formik, enabledFields]);

  useEffect(() => {
    window.addEventListener("createMetaPreset", handleCreateMetaPreset);
    return () =>
      window.removeEventListener("createMetaPreset", handleCreateMetaPreset);
  }, [handleCreateMetaPreset]);

  return (
    <>
      <div
        className="row bg-light p-3 position-sticky z-2 m-0"
        style={{ top: "-16px", zIndex: 99999 }}
        // style={{ backgroundColor: '#FCFAFA', position: 'sticky', top: 0, zIndex: 10 }}
      >
        <div className="col-md-4 mt-0">
          <TextInput
            label="Preset Name"
            name="presetName"
            value={formik.values.presetName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showRequired={true}
            error={formik.errors.presetName as string}
            touched={formik.touched.presetName as boolean}
          />
        </div>
        <div className="col-md-4 mt-0">
          <CustomSelect
            // value={formik.values.visibility}
            value={processPermissionForSelect(formik.values.visibility)}
            // value={prevVideoCredit}
            handleChange={(newValue: any) => {
              handlePermissionChange(
                newValue.map((option: any) => option.value),
                "visibility"
              );
            }}
            label="team"
            multiSelect={true}
            placeholder="Team/Property"
            checkBox={true}
            showRequired={true}
            options={
              productList
                ?.filter(
                  (item: any) =>
                    item?.access === "admin" || item?.access === "write"
                )
                ?.map((item: any) => ({
                  label: item?.name,
                  value: item?.id,
                })) || []
            }
            updatedOptions={
              productList
                ?.filter(
                  (item: any) =>
                    item?.access === "admin" || item?.access === "write"
                )
                ?.map((item: any) => ({
                  label: item?.name,
                  value: item?.id,
                })) || []
            }
            hideArrow={true}
            error={formik.errors.visibility as string}
            touched={formik.touched.visibility as boolean}
            handleBlur={formik.handleBlur}
          />
        </div>
        <div className="col-md-4 mt-0">
          <CustomSelect
            value={formik.values.status}
            handleChange={(newValue: any) => {
              formik.setFieldValue("status", newValue.value);
            }}
            label="Status"
            multiSelect={false}
            placeholder="Select Status"
            showRequired={true}
            options={[
              {
                label: "Active",
                value: 1,
              },
              {
                label: "Inactive",
                value: 2,
              },
            ]}
            updatedOptions={[
              { label: "Active", value: "1" },
              { label: "Inactive", value: "2" },
            ]}
            hideArrow={true}
            error={formik.errors.status as string}
            touched={formik.touched.status as boolean}
          />
        </div>
      </div>
      <hr className="mt-2" />
      <div className=" d-flex">
        <div className="w-100">
          <AllSocialPublish
            isPreset={true}
            isWizard={false}
            handleAllSocialPresets={handleAllSocialPresets}
            item={item}
            presetDetails={
              isEdit ? selectedMedia?.social : selectedMedia?.socials
            }
          />
        </div>
      </div>
    </>
  );
};

export default SocialPresets;
